import { Box, IconButton } from "@mui/material";
import { Typography } from "@mui/material";
import { Stack } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useState, useEffect } from 'react';
import { useTBContext } from "../Contexts/useTBStates";
import { FlightBadge, PreferenceBadge, TeeSheetLegend } from "./TeeSheetBadges";
import {
    type ShotgunSlotOrder,
    matchesShotgunOrder,
    filterRegsForShotgunSlot,
    resolveRoundHoles,
    computeShotgunTeeTimeCount,
} from "./Functions/shotgunTeeSheetUtils";


interface ShotgunTeeSheetProps {
    createTeeTime: (startingTime: string, row: number) => Date;
    roundLocked?: boolean;
}

function normalizeDropPayload(raw: unknown): any[] {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object') return [raw];
    return [];
}


export const ShotgunTeeSheet = ({ createTeeTime, roundLocked = false }: ShotgunTeeSheetProps) => {

    const {
        viewEvent,
        viewEventData, setViewEventData,
        editMode,
        roundValue,
        factor, appTheme
      } = useTBContext();

    const [holesWithB, setHolesWithB] = useState<Set<number>>(() => new Set<number>());

    // Initialise holesWithB from existing registrations that already have B-slot players
    useEffect(() => {
        if (!viewEventData?.registrations || !viewEvent) return;
        const holes = resolveRoundHoles(viewEvent, viewEventData, roundValue);
        const bHoles = new Set<number>();
        for (const hole of holes) {
            const bPlayers = filterRegsForShotgunSlot(
                viewEventData.registrations,
                roundValue,
                hole.number,
                'B',
            );
            if (bPlayers.length > 0) bHoles.add(hole.number);
        }
        setHolesWithB(bHoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roundValue]);

    if (!viewEvent) return null;

    const currentRound = viewEvent.rounds[roundValue - 1];
    const holes = resolveRoundHoles(viewEvent, viewEventData, roundValue);

    if (!holes.length) return null;

    const rowMinHeight = viewEvent.useTeams ? 35 * factor : 25 * factor;
    const regPerTee = currentRound.regPerTeeTime;
    const shotgunTeeTimeIso = createTeeTime(currentRound.startingTime, 0).toISOString();

    const getSlotRegistrations = (holeNumber: number, slotOrder: ShotgunSlotOrder) =>
        filterRegsForShotgunSlot(
            viewEventData.registrations ?? [],
            roundValue,
            holeNumber,
            slotOrder,
        ).sort((a: any, b: any) => {
            if (a.scoreCode < b.scoreCode) return -1;
            if (a.scoreCode > b.scoreCode) return 1;
            return (a.playerName ?? '').localeCompare(b.playerName ?? '');
        });

    const getOpenSlotCount = (holeNumber: number, slotOrder: ShotgunSlotOrder) =>
        Math.max(0, regPerTee - getSlotRegistrations(holeNumber, slotOrder).length);

    const handleDropShotgun = (
        players: any[],
        holeNumber: number,
        slotOrder: ShotgunSlotOrder,
    ) => {
        const openSlots = getOpenSlotCount(holeNumber, slotOrder);
        if (openSlots < players.length) return;

        const updatedRegistrations = viewEventData.registrations.map((registration: any) => {
            if (!players.some((player: any) => player._id === registration._id)) {
                return registration;
            }

            const withoutRound = (registration.teeTimes ?? []).filter(
                (tt: any) => tt.round !== roundValue,
            );
            withoutRound.push({
                round: roundValue,
                teeTime: shotgunTeeTimeIso,
                startingHole: holeNumber,
                shotgunOrder: slotOrder,
            });

            return { ...registration, teeTimes: withoutRound };
        });

        setViewEventData({ ...viewEventData, registrations: updatedRegistrations });
    };

    const handleClearShotgun = (
        registration: any,
        holeNumber: number,
        slotOrder: ShotgunSlotOrder,
    ) => {
        setViewEventData((prev: any) => ({
            ...prev,
            registrations: prev.registrations.map((prevRegistration: any) =>
                registration._id === prevRegistration._id ||
                registration.scoreCode === prevRegistration.scoreCode
                    ? {
                          ...prevRegistration,
                          teeTimes: (prevRegistration.teeTimes ?? []).filter(
                              (teeTime: any) =>
                                  !(
                                      teeTime.round === roundValue &&
                                      teeTime.startingHole === holeNumber &&
                                      matchesShotgunOrder(teeTime.shotgunOrder, slotOrder)
                                  ),
                          ),
                      }
                    : prevRegistration,
            ),
        }));
    };

    const handleRemoveB = (holeNumber: number) => {
        // Clear all B-slot players for this hole then remove hole from holesWithB
        setViewEventData((prev: any) => ({
            ...prev,
            registrations: prev.registrations.map((reg: any) => ({
                ...reg,
                teeTimes: (reg.teeTimes ?? []).filter(
                    (tt: any) =>
                        !(
                            tt.round === roundValue &&
                            tt.startingHole === holeNumber &&
                            tt.shotgunOrder === 'B'
                        ),
                ),
            })),
        }));
        setHolesWithB((prev) => {
            const next = new Set(prev);
            next.delete(holeNumber);
            return next;
        });
    };

    const renderPlayerSlot = (
        registration: any,
        index: number,
        holeNumber: number,
        slotOrder: ShotgunSlotOrder,
        keyPrefix: string,
    ) => (
        <Stack
            key={`${keyPrefix}-${registration._id ?? index}`}
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{
                flex: 2,
                borderBottom: `1px dotted ${appTheme.palette.primary.light}`,
                minHeight: rowMinHeight,
            }}
        >
            <Stack direction='column' alignItems='flex-start' spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ width: '100%', fontSize: 12 * factor }}>
                    {registration.playerName || `Player ${index + 1}`}
                </Typography>
                {registration.teamName && (
                    <Typography sx={{ width: '100%', fontSize: 10 * factor, fontStyle: 'italic' }}>
                        ({registration.teamName})
                    </Typography>
                )}
            </Stack>
            {(registration.division || registration.flight || registration.teeGroupFocusPreference) && (
                <Stack direction='row' spacing={0.5} alignItems='center' sx={{ ml: 0.5, flexShrink: 0 }}>
                    <FlightBadge
                        division={registration.division}
                        flight={registration.flight}
                        divisionIndex={Math.max(
                            0,
                            viewEvent?.flighting?.divisions?.findIndex(
                                (d: any) => d.divisionName === registration.division,
                            ) ?? 0,
                        )}
                        factor={factor}
                    />
                    <PreferenceBadge
                        preference={registration.teeGroupFocusPreference}
                        factor={factor}
                    />
                </Stack>
            )}
            {editMode && !roundLocked && (
                <IconButton
                    onClick={() => handleClearShotgun(registration, holeNumber, slotOrder)}
                    size="small"
                    sx={{ marginLeft: 1 }}
                >
                    <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
            )}
        </Stack>
    );

    const renderBlankSlots = (holeNumber: number, slotOrder: ShotgunSlotOrder, keyPrefix: string) =>
        Array.from({ length: getOpenSlotCount(holeNumber, slotOrder) }).map((_, blankIndex) => (
            <Typography
                key={`${keyPrefix}-blank-${blankIndex + 1}`}
                sx={{
                    flex: 2,
                    borderBottom: `1px dotted ${appTheme.palette.primary.light}`,
                    minHeight: rowMinHeight,
                    fontSize: 12 * factor,
                }}
            >
                {' '}
            </Typography>
        ));

    const renderHoleRow = (
        hole: any,
        holeIndex: number,
        slotOrder: ShotgunSlotOrder,
        labelSuffix: string,
        extraLabelContent?: React.ReactNode,
    ) => {
        const slotRegs = getSlotRegistrations(hole.number, slotOrder);
        const rowKey = slotOrder === 'A' ? `row-${holeIndex}-A` : slotOrder === 'B' ? `row-${holeIndex}-B` : `row-${holeIndex}`;

        return (
            <Stack
                key={rowKey}
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ padding: 1, minHeight: rowMinHeight }}
                onDragOver={(e) => { if (!roundLocked) e.preventDefault(); }}
                onDrop={(e) => {
                    e.preventDefault();
                    if (roundLocked) return;
                    const dropPlayers = normalizeDropPayload(
                        JSON.parse(e.dataTransfer.getData('text/plain')),
                    );
                    if (dropPlayers.length === 0) return;
                    handleDropShotgun(dropPlayers, hole.number, slotOrder);
                }}
            >
                <Stack direction='row' alignItems='center' spacing={0.5} sx={{ flex: 1, minHeight: rowMinHeight }}>
                    <Typography sx={{ fontSize: 14 * factor }}>
                        {hole.number}{labelSuffix}
                    </Typography>
                    {extraLabelContent}
                </Stack>
                {slotRegs.map((registration: any, index: number) =>
                    renderPlayerSlot(
                        registration,
                        index,
                        hole.number,
                        slotOrder,
                        `players-${holeIndex}-${slotOrder ?? 'main'}`,
                    ),
                )}
                {renderBlankSlots(hole.number, slotOrder, `blank-${holeIndex}-${slotOrder ?? 'main'}`)}
            </Stack>
        );
    };

    const teeTimeCount = computeShotgunTeeTimeCount(viewEvent, roundValue);

    return (
        <>
            {holes.map((hole: any, holeIndex: number) => {
                if (holeIndex >= teeTimeCount) return null;

                const hasB = holesWithB.has(hole.number);

                const addBButton = editMode && !roundLocked && !hasB ? (
                    <IconButton
                        size="small"
                        onClick={() => setHolesWithB((prev) => new Set([...prev, hole.number]))}
                        title="Add B group"
                        sx={{ padding: 0.25 }}
                    >
                        <AddIcon sx={{ fontSize: 12 * factor }} />
                    </IconButton>
                ) : null;

                const removeBButton = editMode && !roundLocked ? (
                    <IconButton
                        size="small"
                        onClick={() => handleRemoveB(hole.number)}
                        title="Remove B group"
                        sx={{ padding: 0.25 }}
                    >
                        <RemoveIcon sx={{ fontSize: 12 * factor }} />
                    </IconButton>
                ) : null;

                const aLabel = hasB ? `A (${hole.par})` : ` (${hole.par})`;

                return (
                    <Box key={`teeSet-${holeIndex}`}>
                        {hasB && renderHoleRow(hole, holeIndex, 'B', 'B', removeBButton)}
                        {renderHoleRow(hole, holeIndex, 'A', aLabel, addBButton)}
                    </Box>
                );
            })}
            <TeeSheetLegend
                registrations={viewEventData.registrations ?? []}
                flighting={viewEvent.flighting}
                factor={factor}
            />
        </>
    );
};
